import * as React from 'react';
import Feedback from '@data-platform/user-feedback';
import scribe from '@grab-x/scribe-web-sdk';
import { notification } from 'antd';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useParams,
} from 'react-router-dom';

import { scribeAuthCookie } from '../common/cookies';
import { isValidDomainFamily } from '../common/domains';
import {
  applicationName,
  clientType,
  domainBaseEnvironment,
  isAppLocalMode,
  isProduction,
  USER_FEEDBACK_COMPONENT_ENV,
} from '../common/environment';
import Service from '../lib/App';
import configureAnalytics from '../services/configureAnalytics';
import AccessControlPage from './AccessControlPage';
import LoginPage from './LoginPage';
import RolloutPage from './RolloutPage';
import UserRolesPage from './UserRolesPage';

import './XPortalAppRoutes.css';
import '../global.css';

import { Skeleton } from '@duxton-web/ui';

import AdminPage from './AdminPage';
import Changelog from './Changelog/Changelog';
import { ErrorPage, NotFound } from './ErrorPage';
import Experiments from './ExperimentsPage';
import GroupPage from './GroupPage/GroupPage';
import { Configurations } from './Product/Configurations';
import VariablesSearch from './Search/Variables/VariablesSearch';
import { MainPageSkeleton } from '@/common/components';
import { EXPERIMENTS, getTenant, GXB, GXS } from '@/common/Tenants';
import { FEATURE_FLAGS } from '@/constants';
import { SystemEnvironment } from '@/constants/systemEnviroment';
import { ExperimentContextProvider } from '@/contexts/ExperimentContext';
import { UserRolesContextProvider } from '@/contexts/UserRolesContext';
import { VariableProvider } from '@/contexts/VariableContext';
import WrappedExperimentFormV2 from '@/features/experiment/detail/wrapper';
import AppLayout from '@/features/layout';
import { error } from '@/utils/logging';
import { appendEnvParam } from '@/utils/navigation';
import { getLoggedUserEmail, isAdmin } from '@/utils/user';

configureAnalytics();

export class XPortalAppRoutes extends React.Component<
  // eslint-disable-next-line @typescript-eslint/ban-types
  {},
  {
    isInitialized: boolean;
    environment: SystemEnvironment;
    showLocalSetupModal: boolean;
    enableServiceAuthZ: boolean;
  }
> {
  state = {
    isInitialized: false,
    environment: SystemEnvironment.STAGING,
    showLocalSetupModal: false,
    enableServiceAuthZ: false,
  };

  _forceUpdate = () => this.forceUpdate();

  constructor(props) {
    super(props);

    const searchParams = new URLSearchParams(window.location.search);
    const isBeta: boolean = (searchParams.get('beta') ?? '0') == '1';
    if (isBeta) {
      Service.cache.setIsBetaFeatureEnabled(isBeta);
    }
  }

  async componentDidMount() {
    if (this.isLoggedIn()) {
      try {
        const userEmail = getLoggedUserEmail();
        if (!userEmail) {
          error('Unexpected error: userEmail is null');
          return;
        }
        await this.initializeCache(userEmail);
      } catch (e) {
        this.forceUpdate();
      }
    }

    Service.planner!.subscribeLogout(this._forceUpdate);
    Service.saml!.subscribeLogout(this._forceUpdate);
  }

  componentWillUnmount() {
    Service.planner!.unsubscribeLogout(this._forceUpdate);
    Service.saml!.unsubscribeLogout(this._forceUpdate);
  }

  onLogout = async (): Promise<void> => {
    Service.cache.clearCache();

    notification.success({
      description: 'Please wait while we clear your local data.',
      message: `Logout`,
    });

    const tenant = getTenant();
    switch (tenant) {
      case EXPERIMENTS:
        await Service.planner!.logout();
        break;
      case GXS:
        await Service.saml!.logout();
        break;
      default:
        await Service.planner!.logout();
        break;
    }

    notification.destroy();
    this.forceUpdate();
    window.location.href = '/login?shouldRelogin=true';
  };

  renderSkeleton = () => {
    const items = [];

    for (let index = 0; index < 10; index++) {
      items.push(
        <Skeleton
          key={`skeleton-${index}`}
          active
          paragraph={{
            rows: 20,
          }}
        />,
      );
    }

    return items;
  };

  render(): React.ReactNode {
    const isLoggedIn = this.isLoggedIn();
    const isInitialized: boolean = this.state.isInitialized;
    const { enableServiceAuthZ } = this.state;

    scribe.initialize(applicationName, clientType, isProduction);
    scribe.setAuthToken(scribeAuthCookie());

    if (isLoggedIn) {
      const userEmail = getLoggedUserEmail();
      scribe.setFacets({ email: userEmail, domainBaseEnvironment });
      Feedback.init({
        applicationId: 'GrabX-UI',
        env: USER_FEEDBACK_COMPONENT_ENV,
        userEmail: userEmail,
      });
    }

    return (
      <main className="app x-portal-app-routers">
        {!isLoggedIn && (
          <BrowserRouter>
            <LoginPage onLogin={this.initializeCache} />
          </BrowserRouter>
        )}

        {isLoggedIn &&
          (!isInitialized ? (
            <MainPageSkeleton />
          ) : (
            <BrowserRouter>
              <Routes>
                <Route
                  path=":domainFamily/*"
                  element={
                    <DomainFamilyRoutes
                      enableServiceAuthZ={enableServiceAuthZ}
                      onLogout={this.onLogout}
                    />
                  }
                />
                <Route path="/" element={<Navigate to="/t" replace />} />
              </Routes>
            </BrowserRouter>
          ))}
      </main>
    );
  }

  private initializeCache = async (email: string) => {
    try {
      await Service.cache!.initialize(email);
      const enableServiceAuthZ = Service.cache!.isFeatureFlagOn(
        FEATURE_FLAGS.SERVICE_AUTHZ,
      );
      const alreadyShown =
        localStorage.getItem('local_setup_modal_shown') === '1';
      const showLocalSetupModal =
        isAppLocalMode &&
        Service.cache!.getAllTechFamilies().length === 0 &&
        !alreadyShown;
      this.setState({
        enableServiceAuthZ,
        isInitialized: true,
        showLocalSetupModal,
      });
    } catch (e) {
      console.warn('error at initialization');
      this.setState({ enableServiceAuthZ: false, isInitialized: true });
    }
  };

  private isLoggedIn = (): boolean => {
    const tenant = getTenant();
    switch (tenant) {
      case GXS:
      case GXB:
        return Service.saml!.isLoggedIn();
      case EXPERIMENTS:
        return Service.planner!.isLoggedIn();
      default:
        return Service.planner!.isLoggedIn();
    }
  };
}

function DomainFamilyRoutes({
  enableServiceAuthZ,
  onLogout,
}: {
  enableServiceAuthZ: boolean;
  onLogout: () => Promise<void>;
}) {
  const { domainFamily } = useParams<{ domainFamily: string }>();

  if (!isValidDomainFamily(domainFamily!)) {
    return <ErrorPage />;
  }

  return (
    <UserRolesContextProvider enableServiceAuthZ={enableServiceAuthZ}>
      <ExperimentContextProvider>
        <VariableProvider variable={null}>
          <AppLayout onLogout={onLogout} domainFamily={domainFamily!}>
            <Routes>
              <Route
                path="experiments/:action"
                element={<WrappedExperimentFormV2 />}
              />
              <Route
                path="experiments/:experimentName/:action"
                element={<WrappedExperimentFormV2 />}
              />
              {/* <Route
                path="experiments/:experimentName/:tabName"
                element={<ExperimentDetailSubPage />}
              />
              <Route
                path="experiments/:experimentName"
                element={<ExperimentDetailSubPage />}
              /> */}
              <Route path="experiments" element={<Experiments />} />
              <Route
                path="user-roles"
                element={React.createElement(UserRolesPage as any)}
              />
              <Route path="access-control" element={<AccessControlPage />} />
              <Route
                path="variables/:variableName/rollout/*"
                element={<RolloutPage />}
              />
              <Route
                path="variables/:variableName/rollout"
                element={<RolloutPage />}
              />
              <Route
                path="variables/:variableName/:action"
                element={<Configurations />}
              />
              <Route path="rollout/:variableId" element={<RolloutPage />} />
              <Route path="vars/:variableId" element={<Configurations />} />
              <Route path="variables" element={<Configurations />} />
              <Route path="groups" element={<GroupPage />} />
              <Route path="new-create" element={<WrappedExperimentFormV2 />} />
              <Route path="search/variables" element={<VariablesSearch />} />
              <Route path="changelog" element={<Changelog />} />
              <Route
                path="admin"
                element={
                  isAdmin() ? (
                    <AdminPage />
                  ) : (
                    <Navigate to="not-found" replace />
                  )
                }
              />
              <Route path="not-found" element={<NotFound />} />
              <Route
                path="*"
                element={
                  <Navigate to={`${appendEnvParam('experiments')}`} replace />
                }
              />
            </Routes>
          </AppLayout>
        </VariableProvider>
      </ExperimentContextProvider>
    </UserRolesContextProvider>
  );
}

export default XPortalAppRoutes;
